import React from 'react';
import { IonButton, IonIcon, IonText } from '@ionic/react';
import { attachOutline, chevronDownOutline, chevronUpOutline } from 'ionicons/icons';
import { FileAttachment, AttachmentType } from '@/types/model/inspection-modal';
import { FileResponse } from '@/types/response/InspectionResultResponses';
import AttachmentComponent from '@/components/inspection/common/Attachment';
import { Capacitor } from '@capacitor/core';

interface AttachmentsSectionProps {
  attachments: FileAttachment[];
  onAddAttachment: (type: AttachmentType) => void;
  onDelete: (index: number) => void;
  onFileUpdate: (index: number, file: File) => void;
  onLinkUpdate: (index: number, link: string) => void;
  onExistingFileSelect: (index: number, fileId: string) => void;
  onDescriptionUpdate: (index: number, description: string) => void;
  existingFiles: FileResponse[];
  readOnly?: boolean;
  collapsible?: boolean;
  isExpanded?: boolean;
  onExpandChange?: (isExpanded: boolean) => void;
  existingAttachmentCount?: number;
  title?: string;
}

const AttachmentsSection: React.FC<AttachmentsSectionProps> = ({
  attachments,
  onAddAttachment,
  onDelete,
  onFileUpdate,
  onLinkUpdate,
  onExistingFileSelect,
  onDescriptionUpdate,
  existingFiles,
  readOnly = false,
  collapsible = false,
  isExpanded = true,
  onExpandChange,
  existingAttachmentCount = 0,
  title = 'Attachments',
}) => {
  const isMobilePlatform = Capacitor.getPlatform() !== 'web';
  const hasPersistedAttachments = attachments.some(attachment => attachment.attachmentId !== undefined);

  const handleToggleExpand = () => {
    if (onExpandChange) {
      onExpandChange(!isExpanded);
    }
  };

  const sectionHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px',
    background: 'var(--ion-color-light)',
    borderRadius: '8px',
    cursor: collapsible ? 'pointer' : 'default',
    userSelect: 'none',
  };

  const sectionContent = (
    <div style={{ marginTop: collapsible ? 0 : '12px' }}>
      {/* Existing Attachments Count */}
      {existingAttachmentCount > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <IonText color="medium" style={{ fontSize: '0.9rem' }}>
            <strong>Existing Attachments:</strong> {existingAttachmentCount} file(s)
          </IonText>
        </div>
      )}

      {/* Attachment List Label */}
      {attachments.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <IonText color="medium" style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
            {hasPersistedAttachments ? 'Attachments:' : 'New Attachments:'}
          </IonText>
        </div>
      )}

      {/* Attachment List */}
      {attachments.length > 0 && (
        <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {attachments.map((attachment, index) => (
            <AttachmentComponent
              key={`${attachment.attachmentId ?? 'new'}-${attachment.type ?? 'unknown'}-${attachment.fileId ?? attachment.fileName ?? attachment.link ?? index}`}
              itemId={0}
              index={index}
              attachment={attachment}
              existingFiles={existingFiles}
              readOnly={readOnly}
              onDelete={(_itemId, idx) => onDelete(idx)}
              onFileUpdate={(_itemId, idx, file) => onFileUpdate(idx, file)}
              onLinkUpdate={(_itemId, idx, link) => onLinkUpdate(idx, link)}
              onExistingFileSelect={(_itemId, idx, fileId) => onExistingFileSelect(idx, fileId)}
              onDescriptionUpdate={(_itemId, idx, description) => onDescriptionUpdate(idx, description)}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {attachments.length === 0 && existingAttachmentCount === 0 && (
        <IonText color="medium">
          <p style={{ marginTop: '12px', fontStyle: 'italic' }}>No attachments</p>
        </IonText>
      )}

      {/* Add Buttons */}
      {!readOnly && (
        <>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: attachments.length > 0 ? '12px' : 0 }}>
            <IonButton size="small" fill="outline" onClick={() => onAddAttachment('local-file')}>
              New Upload
            </IonButton>
            {existingFiles.length > 0 && (
              <IonButton size="small" fill="outline" onClick={() => onAddAttachment('existing-resource')}>
                Existing Resource
              </IonButton>
            )}
            <IonButton size="small" fill="outline" onClick={() => onAddAttachment('link')}>
              Add Link
            </IonButton>
            {isMobilePlatform && (
              <IonButton size="small" fill="outline" onClick={() => onAddAttachment('image')}>
                Capture Image
              </IonButton>
            )}
          </div>
          {!isMobilePlatform && (
            <IonText color="medium" style={{ display: 'block', marginTop: '8px', fontSize: '0.8rem' }}>
              Image capture available on app
            </IonText>
          )}
        </>
      )}
    </div>
  );

  if (collapsible) {
    return (
      <div>
        <div style={sectionHeaderStyle} onClick={handleToggleExpand}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', fontWeight: '500' }}>
            <IonIcon icon={attachOutline} />
            {title}
            {(attachments.length + existingAttachmentCount) > 0 && (
              <span style={{ fontSize: '0.75rem', color: 'var(--ion-color-medium)' }}>
                ({attachments.length + existingAttachmentCount})
              </span>
            )}
          </span>
          <IonIcon
            icon={isExpanded ? chevronUpOutline : chevronDownOutline}
            style={{ color: 'var(--ion-color-medium)' }}
          />
        </div>

        {isExpanded && (
          <div style={{ paddingBottom: '8px' }}>
            {sectionContent}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginTop: '24px', marginBottom: '16px' }}>
      <div>
        <IonText>
          <h3>{title}</h3>
        </IonText>
      </div>
      {sectionContent}
    </div>
  );
};

export default AttachmentsSection;
